// SHADOW MARKET™ — Pattern card used in the Academy patterns list.
// Visual style mirrors the rest of the Academy: glass dark card, orange
// accent for primary actions, semantic colors for badges.
import React from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus, ArrowRight } from "lucide-react";
import { PatronDiagrama } from "./PatronDiagrama";
import { useLanguage } from "../../i18n/LanguageContext";
import { getPatronText, getPatronesUi } from "../../i18n/patrones";

const dirBadge = {
  alcista: {
    cls: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
    Icon: TrendingUp,
  },
  bajista: {
    cls: "border-red-400/30 bg-red-400/10 text-red-300",
    Icon: TrendingDown,
  },
  neutro: {
    cls: "border-amber-400/30 bg-amber-400/10 text-amber-300",
    Icon: Minus,
  },
};

const tipoBadge = {
  continuacion: "border-cyan-400/30 bg-cyan-400/10 text-cyan-300",
  reversion: "border-fuchsia-400/30 bg-fuchsia-400/10 text-fuchsia-300",
  neutro: "border-amber-400/30 bg-amber-400/10 text-amber-300",
};

const nivelBadge = {
  basico: "border-white/15 bg-white/[0.05] text-white/60",
  intermedio: "border-orange-400/40 bg-orange-400/10 text-orange-300",
};

export const PatronCard = ({ patron, index = 0 }) => {
  const dir = dirBadge[patron.direccion];
  const { lang } = useLanguage();
  const ui = getPatronesUi(lang);
  const txt = getPatronText(patron.id, lang) || {};
  const nombre = txt.nombre || patron.nombre;
  const descripcion = txt.descripcion || patron.descripcion;

  return (
    <Link
      to={`/academy/patrones/${patron.id}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl transition-all hover:-translate-y-0.5 hover:border-orange-400/40 hover:shadow-[0_0_30px_rgba(255,140,0,0.18)]"
      data-testid={`patron-card-${patron.id}`}
      style={{ transitionDelay: `${(index % 3) * 0.05}s` }}
    >
      {/* DIAGRAM */}
      <div className="relative border-b border-white/10 bg-[#07080f] p-3">
        <PatronDiagrama id={patron.id} />
      </div>

      {/* INFO */}
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3
            className="text-base font-black tracking-tight text-white"
            data-testid={`patron-name-${patron.id}`}
          >
            {nombre}
          </h3>
          <span
            className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] ${nivelBadge[patron.nivel]}`}
          >
            {ui.nivel[patron.nivel]}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] ${tipoBadge[patron.tipo]}`}
          >
            {ui.tipo_badge[patron.tipo]}
          </span>
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] ${dir.cls}`}
          >
            <dir.Icon className="h-2.5 w-2.5" />
            {ui.direccion[patron.direccion]}
          </span>
        </div>

        <p className="text-xs leading-relaxed text-white/55">
          {descripcion.length > 110 ? `${descripcion.slice(0, 110).trim()}…` : descripcion}
        </p>

        <div className="mt-auto pt-2">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-orange-300 transition-colors group-hover:text-orange-200">
            {ui.card_cta}
            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default PatronCard;
